import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Download, 
  Printer, 
  ChevronRight, 
  FileText,
  Building2,
  Target,
  Users,
  DollarSign,
  BarChart3,
  CheckCircle2,
  Calendar,
  Award,
  Briefcase,
  TrendingUp,
  Shield,
  Heart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const DOLProposal = () => {
  const [activeSection, setActiveSection] = useState("cover");
  const contentRef = useRef(null);

  const sections = [
    { id: "cover", title: "Cover Page" },
    { id: "executive-summary", title: "Executive Summary" },
    { id: "statement-of-need", title: "Statement of Need" },
    { id: "target-population", title: "Target Population" },
    { id: "project-description", title: "Project Description" },
    { id: "goals-objectives", title: "Goals & Objectives" },
    { id: "program-design", title: "Program Design" },
    { id: "implementation", title: "Implementation Plan" },
    { id: "organizational-capacity", title: "Organizational Capacity" },
    { id: "partnerships", title: "Strategic Partnerships" },
    { id: "budget", title: "Budget & Justification" },
    { id: "evaluation", title: "Evaluation Plan" },
    { id: "sustainability", title: "Sustainability Plan" },
    { id: "appendices", title: "Appendices" }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    const el = contentRef.current;
    if (!el) return;
    const html2pdf = (await import("html2pdf.js")).default;
    html2pdf().set({
      margin: 10,
      filename: "Soar-Your-Visibility-DOL-Grant-Proposal.pdf",
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    }).from(el).save();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/business-plan" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Business Plan</span>
            </Link>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <Building2 className="w-3 h-3 mr-1" />
                DOL Grant Proposal
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                className="gap-2"
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
              <Button 
                size="sm" 
                onClick={handleDownloadPdf}
                className="gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
              <Link to="/dashboard">
                <Button 
                  size="sm" 
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar - Table of Contents */}
          <aside className="lg:col-span-3 no-print">
            <div className="sticky top-24">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-900">Grant Proposal</h3>
                </div>
                <ScrollArea className="h-[calc(100vh-250px)]">
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        data-testid={`toc-${section.id}`}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                          activeSection === section.id
                            ? "bg-blue-600 text-white font-medium"
                            : "text-slate-600 hover:bg-blue-100 hover:text-slate-900"
                        )}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </ScrollArea>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9" ref={contentRef}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 lg:p-12 proposal-content">
              
              {/* Cover Page */}
              <section id="cover" className="mb-16 pb-16 border-b border-slate-200">
                <div className="text-center">
                  {/* DOL Header */}
                  <div className="mb-8">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
                      <Building2 className="w-4 h-4" />
                      U.S. Department of Labor
                    </div>
                    <h2 className="text-lg text-slate-600">Employment and Training Administration</h2>
                    <h3 className="text-md text-slate-500">Workforce Development Grant Program</h3>
                  </div>

                  {/* Title */}
                  <div className="my-12">
                    <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                      GRANT APPLICATION
                    </h1>
                    <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
                    <h2 className="text-2xl lg:text-3xl font-semibold text-blue-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Soar Your Visibility
                    </h2>
                    <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                      Professional Interview Attire Voucher Program for Workforce Reintegration
                    </p>
                  </div>

                  {/* Funding Request Box */}
                  <div className="bg-gradient-to-r from-blue-900 to-slate-900 text-white rounded-2xl p-8 max-w-xl mx-auto my-12">
                    <div className="text-sm uppercase tracking-wider text-blue-300 mb-2">Funding Request</div>
                    <div className="text-5xl font-bold mb-2">$2,500,000</div>
                    <div className="text-blue-200">Three-Year Grant Period (FY 2026-2029)</div>
                  </div>

                  {/* Applicant Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto text-left mt-12">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Applicant Organization</div>
                      <div className="font-semibold text-slate-900">Soar Your Visibility, Inc.</div>
                      <div className="text-sm text-slate-600">501(c)(3) Nonprofit Organization</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Service Area</div>
                      <div className="font-semibold text-slate-900">State of Colorado</div>
                      <div className="text-sm text-slate-600">Statewide Coverage</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Headquarters</div>
                      <div className="font-semibold text-slate-900">Colorado Springs, Colorado</div>
                      <div className="text-sm text-slate-600">Christina Fraser, Executive Director</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Submission Date</div>
                      <div className="font-semibold text-slate-900">January 2026</div>
                      <div className="text-sm text-slate-600">CFDA: 17.XXX</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Executive Summary */}
              <section id="executive-summary" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Executive Summary
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p className="text-lg leading-relaxed">
                    <strong className="text-slate-900">Soar Your Visibility</strong> respectfully submits this proposal to the U.S. Department of Labor's 
                    Employment and Training Administration for a <strong className="text-blue-700">$2,500,000</strong> grant over three years to implement 
                    a comprehensive Professional Interview Attire Voucher Program serving unemployed and underemployed individuals 
                    across <strong>Colorado</strong>.
                  </p>

                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-6 rounded-r-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Program Mission</h4>
                    <p className="text-blue-800 mb-0">
                      To eliminate the invisible barrier of inadequate interview attire that prevents qualified job seekers 
                      from successfully competing for employment opportunities, thereby accelerating their pathway to economic 
                      self-sufficiency.
                    </p>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Key Program Elements</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-slate-900">Dignity-Centered Approach</div>
                        <div className="text-sm text-slate-600">Voucher-based system allowing personal choice at retail partners</div>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-slate-900">Technology-Enabled</div>
                        <div className="text-sm text-slate-600">QR-code verification system for seamless redemption</div>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-slate-900">WIOA Aligned</div>
                        <div className="text-sm text-slate-600">Full compliance with Workforce Innovation and Opportunity Act</div>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-slate-900">Measurable Outcomes</div>
                        <div className="text-sm text-slate-600">78% target employment rate within 90 days</div>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Projected Impact (3-Year Period)</h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-6">
                    <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="text-3xl font-bold text-amber-700">15,000</div>
                      <div className="text-sm text-amber-600">Job Seekers Served</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-3xl font-bold text-green-700">11,700</div>
                      <div className="text-sm text-green-600">Projected Placements</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-3xl font-bold text-blue-700">200+</div>
                      <div className="text-sm text-blue-600">Retail Partners</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-3xl font-bold text-purple-700">$47M</div>
                      <div className="text-sm text-purple-600">Economic Impact</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Statement of Need */}
              <section id="statement-of-need" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-red-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Statement of Need
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">The Hidden Employment Barrier</h3>
                  
                  <p>
                    While significant federal investments address job training, resume assistance, and interview coaching, 
                    a critical barrier remains largely unaddressed: <strong>appropriate interview attire</strong>. For millions of 
                    Americans facing financial hardship, the cost of professional clothing represents an insurmountable obstacle 
                    to employment.
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 my-6">
                    <h4 className="font-semibold text-red-900 mb-4">National Statistics on Attire Barriers</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold text-red-700">55%</div>
                        <div className="text-sm text-red-800">of employers would not hire a candidate dressed inappropriately</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold text-red-700">65%</div>
                        <div className="text-sm text-red-800">of hiring managers cite appearance as a deciding factor</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold text-red-700">1 in 4</div>
                        <div className="text-sm text-red-800">job seekers report lacking professional interview clothing</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold text-red-700">3x</div>
                        <div className="text-sm text-red-800">more likely for low-income individuals to miss opportunities</div>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Regional Context: Colorado</h3>
                  
                  <p>
                    In Colorado, the need for interview attire support is significant despite relatively strong overall economic indicators:
                  </p>
                  
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 my-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-blue-700">3.8%</span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">Unemployment Rate</div>
                          <div className="text-sm text-slate-600">Below national average of 4.4%, representing approximately 123,800 unemployed Coloradans actively seeking work</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-red-700">9.4%</span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">Poverty Rate</div>
                          <div className="text-sm text-slate-600">Approximately 550,000 residents live below the federal poverty line; high cost of living increases effective poverty</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-amber-700">13K</span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">TANF Recipients</div>
                          <div className="text-sm text-slate-600">Over 13,000 families receiving Colorado Works temporary assistance, with caseloads rising throughout 2024</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-purple-700">7.2K</span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">Returning Citizens</div>
                          <div className="text-sm text-slate-600">Approximately 7,200 individuals released from Colorado correctional facilities annually, seeking workforce reintegration</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-green-700">12K</span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">Unemployed Veterans</div>
                          <div className="text-sm text-slate-600">With 348,000+ veterans (7.2% of adult population) and 3.5% veteran unemployment rate, approximately 12,000 Colorado veterans are actively job seeking</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-lg font-bold text-pink-700">HIGH</span>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">Cost of Living</div>
                          <div className="text-sm text-slate-600">Colorado's high housing and childcare costs mean many working families struggle despite income above federal poverty thresholds</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p>
                    Colorado's strong labor market masks significant pockets of need. While the state's unemployment rate 
                    is below the national average, the high cost of living—particularly in the Denver metropolitan area—means 
                    that many residents struggle to afford professional clothing even when employed. The state's growing 
                    population of returning citizens and the substantial veteran community present particular opportunities 
                    for workforce development investment.
                  </p>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">The First Impression Gap</h3>
                  
                  <p>
                    Research from the Journal of Applied Psychology demonstrates that interviewers form initial impressions 
                    within the first 7 seconds of meeting a candidate. These impressions are heavily influenced by physical 
                    appearance, including attire. For job seekers who cannot afford appropriate clothing, this creates a 
                    systemic disadvantage that undermines their qualifications and preparation.
                  </p>

                  <blockquote className="border-l-4 border-blue-500 pl-4 italic text-slate-700 my-6">
                    "I had all the skills for the job, but I could see the interviewer's face change when I walked in. 
                    I was wearing the best I had, but it wasn't good enough. That's when I realized clothes matter 
                    more than I thought."
                    <footer className="text-sm text-slate-500 mt-2">— Program participant, Austin TX</footer>
                  </blockquote>
                </div>
              </section>

              {/* Target Population */}
              <section id="target-population" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Target Population
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p>
                    Soar Your Visibility will serve individuals who meet the following eligibility criteria, 
                    with priority given to populations facing multiple barriers to employment:
                  </p>

                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">Eligibility Requirements</h3>
                  
                  <div className="bg-slate-50 rounded-xl p-6 my-6">
                    <ul className="space-y-3 mb-0">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Currently unemployed or underemployed (working fewer than 20 hours/week)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Household income at or below 200% of Federal Poverty Level</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Actively seeking employment or enrolled in approved job training program</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Referred by approved workforce development agency or self-certified</span>
                      </li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Priority Populations</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-slate-900">Veterans</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-0">
                        Transitioning service members and veterans seeking civilian employment
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-slate-900">Returning Citizens</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-0">
                        Individuals re-entering the workforce after incarceration
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-5 h-5 text-red-600" />
                        <span className="font-semibold text-slate-900">TANF Recipients</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-0">
                        Families receiving Temporary Assistance for Needy Families
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-slate-900">Displaced Workers</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-0">
                        Workers affected by plant closings, layoffs, or industry decline
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-slate-900">Foster Care Alumni</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-0">
                        Young adults aging out of the foster care system
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-5 h-5 text-pink-600" />
                        <span className="font-semibold text-slate-900">DV Survivors</span>
                      </div>
                      <p className="text-sm text-slate-600 mb-0">
                        Survivors of domestic violence seeking financial independence
                      </p>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Projected Demographics (Annual)</h3>
                  
                  <table className="w-full border-collapse my-6">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 p-3 text-left">Population Segment</th>
                        <th className="border border-slate-200 p-3 text-center">Year 1</th>
                        <th className="border border-slate-200 p-3 text-center">Year 2</th>
                        <th className="border border-slate-200 p-3 text-center">Year 3</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 p-3">TANF Recipients</td>
                        <td className="border border-slate-200 p-3 text-center">1,200</td>
                        <td className="border border-slate-200 p-3 text-center">1,500</td>
                        <td className="border border-slate-200 p-3 text-center">1,800</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Veterans</td>
                        <td className="border border-slate-200 p-3 text-center">600</td>
                        <td className="border border-slate-200 p-3 text-center">750</td>
                        <td className="border border-slate-200 p-3 text-center">900</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Returning Citizens</td>
                        <td className="border border-slate-200 p-3 text-center">800</td>
                        <td className="border border-slate-200 p-3 text-center">1,000</td>
                        <td className="border border-slate-200 p-3 text-center">1,200</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Displaced Workers</td>
                        <td className="border border-slate-200 p-3 text-center">1,000</td>
                        <td className="border border-slate-200 p-3 text-center">1,250</td>
                        <td className="border border-slate-200 p-3 text-center">1,500</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Other Eligible</td>
                        <td className="border border-slate-200 p-3 text-center">900</td>
                        <td className="border border-slate-200 p-3 text-center">1,000</td>
                        <td className="border border-slate-200 p-3 text-center">1,100</td>
                      </tr>
                      <tr className="bg-blue-50 font-semibold">
                        <td className="border border-slate-200 p-3">Total</td>
                        <td className="border border-slate-200 p-3 text-center">4,500</td>
                        <td className="border border-slate-200 p-3 text-center">5,500</td>
                        <td className="border border-slate-200 p-3 text-center">6,500</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Project Description */}
              <section id="project-description" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Project Description
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p className="text-lg">
                    Soar Your Visibility is an innovative workforce development program that removes clothing-related 
                    barriers to employment through a technology-enabled voucher system, extensive retail partnerships, 
                    and comprehensive wraparound services.
                  </p>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Program Components</h3>

                  <div className="space-y-6 my-6">
                    <div className="bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-xl p-6">
                      <h4 className="font-semibold text-blue-900 text-lg mb-3">1. Digital Voucher System</h4>
                      <p className="text-slate-600 mb-4">
                        Our proprietary platform issues secure digital vouchers with unique QR codes that participants 
                        can present at any approved retail partner. The system provides:
                      </p>
                      <ul className="space-y-2 text-slate-600">
                        <li>• Real-time voucher verification at point of sale</li>
                        <li>• Fraud prevention through unique codes and beneficiary verification</li>
                        <li>• Instant balance tracking and partial redemption capability</li>
                        <li>• Complete audit trail for compliance reporting</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-white border border-green-200 rounded-xl p-6">
                      <h4 className="font-semibold text-green-900 text-lg mb-3">2. Retail Partner Network</h4>
                      <p className="text-slate-600 mb-4">
                        We maintain relationships with diverse retail partners to ensure geographic coverage and 
                        appropriate options for all participants:
                      </p>
                      <ul className="space-y-2 text-slate-600">
                        <li>• Major retailers (JCPenney, Kohl's, Target, Macy's)</li>
                        <li>• Discount retailers (Ross, TJ Maxx, Marshall's)</li>
                        <li>• Professional specialty stores (Men's Wearhouse, Ann Taylor)</li>
                        <li>• Nonprofit partners (Dress to Impress, Goodwill)</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-xl p-6">
                      <h4 className="font-semibold text-amber-900 text-lg mb-3">3. Wraparound Support Services</h4>
                      <p className="text-slate-600 mb-4">
                        Beyond clothing, we connect participants with complementary services:
                      </p>
                      <ul className="space-y-2 text-slate-600">
                        <li>• Professional image consultation and styling guidance</li>
                        <li>• Interview preparation workshops</li>
                        <li>• Resume review and career coaching referrals</li>
                        <li>• Connection to additional workforce development resources</li>
                      </ul>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Voucher Value Structure</h3>
                  
                  <table className="w-full border-collapse my-6">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 p-3 text-left">Tier</th>
                        <th className="border border-slate-200 p-3 text-center">Value</th>
                        <th className="border border-slate-200 p-3 text-left">Criteria</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Basic</td>
                        <td className="border border-slate-200 p-3 text-center">$200</td>
                        <td className="border border-slate-200 p-3">Entry-level positions, part-time roles</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Standard</td>
                        <td className="border border-slate-200 p-3 text-center">$350</td>
                        <td className="border border-slate-200 p-3">Professional positions, multiple interviews expected</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Professional</td>
                        <td className="border border-slate-200 p-3 text-center">$500</td>
                        <td className="border border-slate-200 p-3">Executive roles, specialized industries</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Goals & Objectives */}
              <section id="goals-objectives" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Goals & Objectives
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">Program Goals</h3>
                  
                  <div className="space-y-4 my-6">
                    <div className="bg-white border-2 border-blue-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-blue-700">1</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 text-lg">Increase Employment Outcomes</h4>
                          <p className="text-slate-600 mb-0">
                            Improve employment rates among program participants to 78% within 90 days of receiving 
                            interview attire, compared to the regional average of 52% for similar populations.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-green-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-green-700">2</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 text-lg">Expand Program Reach</h4>
                          <p className="text-slate-600 mb-0">
                            Serve 15,000 unemployed and underemployed individuals over the three-year grant period, 
                            with particular focus on priority populations facing multiple barriers.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-amber-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-amber-700">3</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 text-lg">Build Sustainable Infrastructure</h4>
                          <p className="text-slate-600 mb-0">
                            Establish a network of 200+ retail partners and secure diversified funding to ensure 
                            program sustainability beyond the grant period.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">SMART Objectives</h3>
                  
                  <table className="w-full border-collapse my-6">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 p-3 text-left">Objective</th>
                        <th className="border border-slate-200 p-3 text-center">Year 1</th>
                        <th className="border border-slate-200 p-3 text-center">Year 2</th>
                        <th className="border border-slate-200 p-3 text-center">Year 3</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 p-3">Participants Served</td>
                        <td className="border border-slate-200 p-3 text-center">4,500</td>
                        <td className="border border-slate-200 p-3 text-center">5,500</td>
                        <td className="border border-slate-200 p-3 text-center">6,500</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Employment Rate (90-day)</td>
                        <td className="border border-slate-200 p-3 text-center">70%</td>
                        <td className="border border-slate-200 p-3 text-center">75%</td>
                        <td className="border border-slate-200 p-3 text-center">78%</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Voucher Redemption Rate</td>
                        <td className="border border-slate-200 p-3 text-center">80%</td>
                        <td className="border border-slate-200 p-3 text-center">85%</td>
                        <td className="border border-slate-200 p-3 text-center">90%</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Retail Partners</td>
                        <td className="border border-slate-200 p-3 text-center">75</td>
                        <td className="border border-slate-200 p-3 text-center">150</td>
                        <td className="border border-slate-200 p-3 text-center">200+</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Client Satisfaction</td>
                        <td className="border border-slate-200 p-3 text-center">85%</td>
                        <td className="border border-slate-200 p-3 text-center">90%</td>
                        <td className="border border-slate-200 p-3 text-center">92%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Program Design */}
              <section id="program-design" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Program Design
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">Service Delivery Model</h3>
                  
                  <div className="relative my-8">
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-blue-200"></div>
                    <div className="space-y-8">
                      <div className="relative pl-16">
                        <div className="absolute left-3 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                        <div className="bg-white border border-slate-200 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900">Referral & Intake</h4>
                          <p className="text-sm text-slate-600 mb-0">
                            Participants referred by American Job Centers, community organizations, or self-referral. 
                            Initial eligibility screening completed within 24 hours.
                          </p>
                        </div>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-3 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                        <div className="bg-white border border-slate-200 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900">Eligibility Verification</h4>
                          <p className="text-sm text-slate-600 mb-0">
                            Documentation review, income verification, employment status confirmation. 
                            Completed within 3 business days.
                          </p>
                        </div>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-3 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                        <div className="bg-white border border-slate-200 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900">Needs Assessment</h4>
                          <p className="text-sm text-slate-600 mb-0">
                            Individual assessment determines voucher tier, preferred retailers, and any 
                            additional support services needed.
                          </p>
                        </div>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-3 w-7 h-7 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold text-sm">4</div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <h4 className="font-semibold text-amber-900">Voucher Issuance</h4>
                          <p className="text-sm text-amber-800 mb-0">
                            Digital voucher with QR code issued via email/SMS. Valid for 90 days. 
                            Participant receives retailer directory and styling guide.
                          </p>
                        </div>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-3 w-7 h-7 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">5</div>
                        <div className="bg-white border border-slate-200 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900">Shopping Experience</h4>
                          <p className="text-sm text-slate-600 mb-0">
                            Participant visits approved retailer, selects items, and presents voucher 
                            at checkout. QR code scanned for instant verification.
                          </p>
                        </div>
                      </div>
                      <div className="relative pl-16">
                        <div className="absolute left-3 w-7 h-7 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">6</div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h4 className="font-semibold text-green-900">Follow-Up & Outcome Tracking</h4>
                          <p className="text-sm text-green-800 mb-0">
                            30, 60, and 90-day follow-up to track employment outcomes. 
                            Ongoing support and referrals as needed.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Implementation Plan */}
              <section id="implementation" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-cyan-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Implementation Plan
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">Three-Year Timeline</h3>

                  <div className="space-y-6 my-6">
                    <div className="border-l-4 border-blue-500 pl-6">
                      <h4 className="font-semibold text-blue-900 text-lg">Year 1: Foundation & Pilot (Months 1-12)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="font-medium text-slate-700 mb-2">Q1 (Months 1-3)</p>
                          <ul className="text-sm space-y-1">
                            <li>• Staff hiring and training</li>
                            <li>• Technology platform deployment</li>
                            <li>• Initial partner recruitment (25 retailers)</li>
                            <li>• MOU execution with referral partners</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-slate-700 mb-2">Q2-Q4 (Months 4-12)</p>
                          <ul className="text-sm space-y-1">
                            <li>• Pilot launch in primary service area</li>
                            <li>• Serve 4,500 participants</li>
                            <li>• Expand partner network to 75</li>
                            <li>• Process refinement based on feedback</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="border-l-4 border-green-500 pl-6">
                      <h4 className="font-semibold text-green-900 text-lg">Year 2: Expansion (Months 13-24)</h4>
                      <ul className="text-sm space-y-1 mt-4">
                        <li>• Geographic expansion to additional counties/regions</li>
                        <li>• Serve 5,500 participants</li>
                        <li>• Grow partner network to 150 retailers</li>
                        <li>• Launch enhanced mobile application</li>
                        <li>• Establish employer engagement program</li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-amber-500 pl-6">
                      <h4 className="font-semibold text-amber-900 text-lg">Year 3: Scale & Sustainability (Months 25-36)</h4>
                      <ul className="text-sm space-y-1 mt-4">
                        <li>• Full regional coverage achieved</li>
                        <li>• Serve 6,500 participants</li>
                        <li>• 200+ retail partners active</li>
                        <li>• Diversified funding secured for sustainability</li>
                        <li>• Model documentation for replication</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Organizational Capacity */}
              <section id="organizational-capacity" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Organizational Capacity
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p>
                    Soar Your Visibility, Inc. is a 501(c)(3) nonprofit organization with demonstrated capacity 
                    to implement workforce development programs at scale.
                  </p>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Key Staff Positions</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900">Program Director (1.0 FTE)</h4>
                      <p className="text-sm text-slate-600 mb-0">
                        Overall program oversight, stakeholder relations, strategic planning, reporting to DOL
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900">Operations Manager (1.0 FTE)</h4>
                      <p className="text-sm text-slate-600 mb-0">
                        Day-to-day operations, staff supervision, quality assurance, partner coordination
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900">Eligibility Specialists (4.0 FTE)</h4>
                      <p className="text-sm text-slate-600 mb-0">
                        Intake, eligibility verification, voucher issuance, participant support
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900">Partner Relations (2.0 FTE)</h4>
                      <p className="text-sm text-slate-600 mb-0">
                        Retail partner recruitment, onboarding, relationship management, issue resolution
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900">Data Analyst (1.0 FTE)</h4>
                      <p className="text-sm text-slate-600 mb-0">
                        Outcome tracking, reporting, evaluation support, compliance monitoring
                      </p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900">Administrative Support (1.0 FTE)</h4>
                      <p className="text-sm text-slate-600 mb-0">
                        Office management, scheduling, document processing, customer service
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Strategic Partnerships */}
              <section id="partnerships" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-pink-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Strategic Partnerships
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">Colorado Referral Partners</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-blue-900">Colorado Workforce Centers</h4>
                      <p className="text-sm text-blue-700 mb-0">60+ locations statewide serving unemployed Coloradans</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-green-900">Colorado Works (TANF)</h4>
                      <p className="text-sm text-green-700 mb-0">Colorado's TANF program serving 13,000+ families</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-amber-900">Colorado DVBE</h4>
                      <p className="text-sm text-amber-700 mb-0">Division of Veterans Affairs employment services</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-purple-900">CO Dept. of Corrections</h4>
                      <p className="text-sm text-purple-700 mb-0">Reentry programs for 7,200+ annual releases</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-red-900">Community Colleges of CO</h4>
                      <p className="text-sm text-red-700 mb-0">13 colleges with career services programs</p>
                    </div>
                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-center">
                      <h4 className="font-semibold text-cyan-900">Mile High United Way</h4>
                      <p className="text-sm text-cyan-700 mb-0">2-1-1 referrals and workforce programs</p>
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Letters of Support</h3>
                  <p>
                    Attached in the appendices are letters of support from:
                  </p>
                  <ul>
                    <li>Colorado Department of Labor and Employment (CDLE)</li>
                    <li>Denver Workforce Services / Adams County Workforce Center</li>
                    <li>Rocky Mountain Regional VA Medical Center</li>
                    <li>Colorado Department of Corrections - Reentry Services</li>
                    <li>Major retail partners (JCPenney, Goodwill Industries of Denver, Dress to Impress Denver)</li>
                    <li>Denver Foundation / Colorado Trust</li>
                  </ul>
                </div>
              </section>

              {/* Budget */}
              <section id="budget" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Budget & Justification
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-4">Three-Year Budget Summary</h3>
                  
                  <table className="w-full border-collapse my-6">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 p-3 text-left">Category</th>
                        <th className="border border-slate-200 p-3 text-right">Year 1</th>
                        <th className="border border-slate-200 p-3 text-right">Year 2</th>
                        <th className="border border-slate-200 p-3 text-right">Year 3</th>
                        <th className="border border-slate-200 p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Personnel</td>
                        <td className="border border-slate-200 p-3 text-right">$285,000</td>
                        <td className="border border-slate-200 p-3 text-right">$295,000</td>
                        <td className="border border-slate-200 p-3 text-right">$305,000</td>
                        <td className="border border-slate-200 p-3 text-right font-medium">$885,000</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Fringe Benefits (28%)</td>
                        <td className="border border-slate-200 p-3 text-right">$79,800</td>
                        <td className="border border-slate-200 p-3 text-right">$82,600</td>
                        <td className="border border-slate-200 p-3 text-right">$85,400</td>
                        <td className="border border-slate-200 p-3 text-right font-medium">$247,800</td>
                      </tr>
                      <tr className="bg-amber-50">
                        <td className="border border-slate-200 p-3 font-semibold text-amber-900">Voucher Distribution</td>
                        <td className="border border-slate-200 p-3 text-right text-amber-900">$315,000</td>
                        <td className="border border-slate-200 p-3 text-right text-amber-900">$385,000</td>
                        <td className="border border-slate-200 p-3 text-right text-amber-900">$455,000</td>
                        <td className="border border-slate-200 p-3 text-right font-bold text-amber-900">$1,155,000</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Technology & Equipment</td>
                        <td className="border border-slate-200 p-3 text-right">$45,000</td>
                        <td className="border border-slate-200 p-3 text-right">$25,000</td>
                        <td className="border border-slate-200 p-3 text-right">$20,000</td>
                        <td className="border border-slate-200 p-3 text-right font-medium">$90,000</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Travel & Training</td>
                        <td className="border border-slate-200 p-3 text-right">$15,000</td>
                        <td className="border border-slate-200 p-3 text-right">$18,000</td>
                        <td className="border border-slate-200 p-3 text-right">$20,000</td>
                        <td className="border border-slate-200 p-3 text-right font-medium">$53,000</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Supplies & Materials</td>
                        <td className="border border-slate-200 p-3 text-right">$8,000</td>
                        <td className="border border-slate-200 p-3 text-right">$9,000</td>
                        <td className="border border-slate-200 p-3 text-right">$10,000</td>
                        <td className="border border-slate-200 p-3 text-right font-medium">$27,000</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3 font-medium">Contractual (Evaluation)</td>
                        <td className="border border-slate-200 p-3 text-right">$12,000</td>
                        <td className="border border-slate-200 p-3 text-right">$15,000</td>
                        <td className="border border-slate-200 p-3 text-right">$15,000</td>
                        <td className="border border-slate-200 p-3 text-right font-medium">$42,000</td>
                      </tr>
                      <tr className="bg-blue-50">
                        <td className="border border-slate-200 p-3 font-bold text-blue-900">TOTAL DIRECT COSTS</td>
                        <td className="border border-slate-200 p-3 text-right font-bold text-blue-900">$759,800</td>
                        <td className="border border-slate-200 p-3 text-right font-bold text-blue-900">$829,600</td>
                        <td className="border border-slate-200 p-3 text-right font-bold text-blue-900">$910,400</td>
                        <td className="border border-slate-200 p-3 text-right font-bold text-blue-900">$2,499,800</td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 my-6">
                    <h4 className="font-semibold text-green-900 mb-2">Cost-Effectiveness</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-green-700">$167</div>
                        <div className="text-sm text-green-600">Cost per participant</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-700">$214</div>
                        <div className="text-sm text-green-600">Cost per placement</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-700">46%</div>
                        <div className="text-sm text-green-600">Direct to participants</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-700">$3.20</div>
                        <div className="text-sm text-green-600">ROI per $1 invested</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Evaluation Plan */}
              <section id="evaluation" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Evaluation Plan
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p>
                    A comprehensive evaluation will be conducted by an independent third-party evaluator to assess 
                    program implementation, outcomes, and impact.
                  </p>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Key Performance Indicators</h3>
                  
                  <table className="w-full border-collapse my-6">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 p-3 text-left">Indicator</th>
                        <th className="border border-slate-200 p-3 text-center">Target</th>
                        <th className="border border-slate-200 p-3 text-left">Data Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 p-3">Employment rate (90-day)</td>
                        <td className="border border-slate-200 p-3 text-center">78%</td>
                        <td className="border border-slate-200 p-3">Follow-up surveys, wage records</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Job retention (6-month)</td>
                        <td className="border border-slate-200 p-3 text-center">70%</td>
                        <td className="border border-slate-200 p-3">Wage records, employer verification</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Voucher redemption rate</td>
                        <td className="border border-slate-200 p-3 text-center">85%+</td>
                        <td className="border border-slate-200 p-3">Program database</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Participant satisfaction</td>
                        <td className="border border-slate-200 p-3 text-center">90%+</td>
                        <td className="border border-slate-200 p-3">Post-service surveys</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 p-3">Average wage at placement</td>
                        <td className="border border-slate-200 p-3 text-center">$15+/hr</td>
                        <td className="border border-slate-200 p-3">Employment verification</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Sustainability Plan */}
              <section id="sustainability" className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Sustainability Plan
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p>
                    Soar Your Visibility is committed to building a sustainable program that continues serving 
                    job seekers beyond the grant period through diversified funding and strategic partnerships.
                  </p>

                  <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Sustainability Strategies</h3>
                  
                  <div className="space-y-4 my-6">
                    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-blue-700">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Diversified Funding</h4>
                        <p className="text-sm text-slate-600 mb-0">
                          Pursue additional federal grants (HHS, VA), state workforce funding, foundation grants, 
                          and corporate sponsorships to create multiple revenue streams.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-green-700">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Retail Partner Investment</h4>
                        <p className="text-sm text-slate-600 mb-0">
                          Develop corporate partnership program where retailers contribute to the voucher fund 
                          in exchange for customer acquisition and CSR recognition.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-amber-700">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Employer Partnership Program</h4>
                        <p className="text-sm text-slate-600 mb-0">
                          Engage employers to sponsor vouchers for candidates they are interviewing, 
                          creating a pipeline for qualified, professionally-presented candidates.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Appendices */}
              <section id="appendices" className="mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Appendices
                  </h2>
                </div>

                <div className="prose max-w-none text-slate-600">
                  <p>The following documents are attached to this proposal:</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Appendix A</h4>
                      <p className="text-sm text-slate-600 mb-0">Letters of Support from Partner Organizations</p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Appendix B</h4>
                      <p className="text-sm text-slate-600 mb-0">Organizational Chart and Staff Resumes</p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Appendix C</h4>
                      <p className="text-sm text-slate-600 mb-0">Detailed Budget Narrative</p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Appendix D</h4>
                      <p className="text-sm text-slate-600 mb-0">Logic Model and Theory of Change</p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Appendix E</h4>
                      <p className="text-sm text-slate-600 mb-0">Retail Partner Commitments</p>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-900 mb-2">Appendix F</h4>
                      <p className="text-sm text-slate-600 mb-0">501(c)(3) Determination Letter and Financial Audit</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8 text-center">
                    <h4 className="font-semibold text-blue-900 mb-2">Thank You for Your Consideration</h4>
                    <p className="text-blue-700 mb-4">
                      Soar Your Visibility is committed to helping America's job seekers present their best selves 
                      and achieve economic self-sufficiency. We look forward to the opportunity to partner with 
                      the Department of Labor in this important mission.
                    </p>
                    <Link to="/dashboard">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        View Program Dashboard
                      </Button>
                    </Link>
                  </div>
                </div>
              </section>

            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default DOLProposal;
